import { useTranslation } from '@rhino-project/core/i18n';
import { Children, ReactNode, useCallback, useMemo } from 'react';

import { useModelCreateContext } from '@rhino-project/core/hooks';
import { useFormContext } from 'react-hook-form';
import { IconButton } from '../buttons';
import {
  useGlobalComponentForModel,
  useOverrides
} from '@rhino-project/core/hooks';
import { getModelShowPath } from '@rhino-project/core/utils';
import { useNavigate } from '@tanstack/react-router';
import { useBackHistory, useBaseOwnerPath } from '../../hooks';

// The saved record returned by the create mutation; its shape is the model's
// record, which is dynamic here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SavedRecord = any;

interface ModelCreateActionProps {
  children?: ReactNode;
  onSave?: (data: SavedRecord) => void;
  onCancel?: () => void;
  [key: string]: unknown;
}

export const ModelCreateActionSave = ({
  children,
  onSave,
  ...props
}: ModelCreateActionProps) => {
  const { t } = useTranslation();
  const { isPending, mutate } = useModelCreateContext();
  const { handleSubmit } = useFormContext();

  const onSuccess = useCallback(
    (data: SavedRecord) => {
      if (onSave) return onSave(data);
    },
    [onSave]
  );

  const handleClick = useCallback(
    () => handleSubmit((values) => mutate(values, { onSuccess }))(),
    [mutate, handleSubmit, onSuccess]
  );

  return (
    <IconButton
      color="primary"
      icon="bi:save"
      isPending={isPending}
      onPress={handleClick}
      {...props}
    >
      {children || t('rhino.frontend.messages.save')}
    </IconButton>
  );
};

export const ModelCreateActionSaveShow = ({
  onSave,
  ...props
}: ModelCreateActionProps) => {
  const { model } = useModelCreateContext();
  const navigate = useNavigate();
  const { build } = useBaseOwnerPath();

  const handleSave = useCallback(
    (data: SavedRecord) => {
      if (onSave) onSave(data);

      // From the model, not the current URL: this runs on the create page
      // (<model>/new) and in the create modal on the index (<model>). The path
      // is built at runtime, so it doesn't match the typed route union.
      navigate({
        to: build(getModelShowPath(model, data.id))
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any);
    },
    [onSave, navigate, build, model]
  );

  return <ModelCreateActionSave onSave={handleSave} {...props} />;
};

export const ModelCreateActionSaveBack = ({
  onSave,
  ...props
}: ModelCreateActionProps) => {
  const backHistory = useBackHistory();
  const handleSave = useCallback(
    (data: SavedRecord) => {
      if (onSave) onSave(data);

      backHistory();
    },
    [backHistory, onSave]
  );

  return <ModelCreateActionSave onSave={handleSave} {...props} />;
};

export const ModelCreateActionSaveAnother = ({
  children,
  onSave,
  ...props
}: ModelCreateActionProps) => {
  const { t } = useTranslation();
  const { reset } = useFormContext();

  const handleSave = useCallback(
    (data: SavedRecord) => {
      if (onSave) onSave(data);

      reset();
    },
    [onSave, reset]
  );

  return (
    <ModelCreateActionSave onSave={handleSave} {...props}>
      {children || t('rhino.frontend.messages.save_add_another')}
    </ModelCreateActionSave>
  );
};

export const ModelCreateActionCancel = ({
  children,
  onCancel,
  ...props
}: ModelCreateActionProps) => {
  const { t } = useTranslation();
  const backHistory = useBackHistory();
  const handleClick = useCallback(
    () => (onCancel ? onCancel() : backHistory()),
    [onCancel, backHistory]
  );

  return (
    <IconButton outline icon="bi:x-square" onPress={handleClick} {...props}>
      {children || t('rhino.frontend.messages.cancel')}
    </IconButton>
  );
};

const defaultComponents = {
  ModelCreateActionCancel,
  ModelCreateActionSave: ModelCreateActionSaveShow
};

export interface ModelCreateActionsProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  overrides?: Record<string, any>;
  actions?: ReactNode;
  append?: boolean;
  prepend?: boolean;
  hasCancel?: boolean;
  [key: string]: unknown;
}

export const ModelCreateActionsBase = ({
  overrides,
  actions,
  append = false,
  prepend = false,
  hasCancel = false,
  ...props
}: ModelCreateActionsProps) => {
  // eslint-disable-next-line @eslint-react/static-components
  const { ModelCreateActionCancel, ModelCreateActionSave } = useOverrides(
    defaultComponents,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    overrides as Record<string, any>
  );

  const computedDefaultActions = useMemo(
    () =>
      [
        // eslint-disable-next-line @eslint-react/static-components
        hasCancel && <ModelCreateActionCancel key="cancel" />,
        // eslint-disable-next-line @eslint-react/static-components
        <ModelCreateActionSave key="save" />
      ].filter(Boolean),
    // eslint-disable-next-line @eslint-react/exhaustive-deps, react-hooks/exhaustive-deps
    [hasCancel]
  );

  const computedActions = useMemo(() => {
    if (!actions) return computedDefaultActions;

    if (append)
      // eslint-disable-next-line @eslint-react/no-children-to-array
      return [...computedDefaultActions, ...Children.toArray(actions)];

    if (prepend)
      // eslint-disable-next-line @eslint-react/no-children-to-array
      return [...Children.toArray(actions), ...computedDefaultActions];

    return actions;
  }, [actions, append, prepend, computedDefaultActions]);

  return (
    <div className="flex flex-row flex-wrap justify-between mb-3" {...props}>
      {
        // eslint-disable-next-line @eslint-react/no-children-map
        Children.map(computedActions, (action) => action)
      }
    </div>
  );
};

export const ModelCreateActions = (props: ModelCreateActionsProps) =>
  useGlobalComponentForModel(
    'ModelCreateActions',
    ModelCreateActionsBase,
    props
  );
